import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import Tags from "@yaireo/tagify/dist/react.tagify";

window.React = React;

const MARKETING_PIXEL_ID_PATTERN = /^[A-Za-z0-9_\-]+$/;

export const isValidGoogleTrackingId = (trackingId) => {
  if (!trackingId || trackingId.trim().length === 0) return false;
  if (!trackingId.startsWith("UA-") && !trackingId.startsWith("GTM-") && !trackingId.startsWith("G-")) return false;

  return /^[A-Z0-9\-]+$/.test(trackingId);
};

export const isValidFacebookPixelId = (pixelId) => {
  if (!pixelId || pixelId.trim().length === 0) return false;

  return /^\d+$/.test(pixelId);
};

export const isValidMarketingPixelId = (pixelId) => {
  if (!pixelId || pixelId.trim().length === 0) return false;

  return MARKETING_PIXEL_ID_PATTERN.test(pixelId);
};

const upperCaseTag = (tagData) => {
  tagData.value = tagData.value.toUpperCase();
  return tagData;
};

export const MARKETING_PIXEL_KINDS = {
  google: {
    placeholder: "Add Google tracking ID (UA-, G-, or GTM-)",
    isValid: isValidGoogleTrackingId,
    settings: {
      pattern: /^(UA-|G-|GTM-)[A-Z0-9\-]+$/,
      validate: (tagData) => isValidGoogleTrackingId(tagData.value.toUpperCase()),
      transformTag: upperCaseTag,
    },
  },
  facebook: {
    placeholder: "Add Facebook Pixel ID (e.g., 123456789012345)",
    isValid: isValidFacebookPixelId,
    settings: { pattern: /^\d+$/, validate: (tagData) => isValidFacebookPixelId(tagData.value) },
  },
  tiktok: {
    placeholder: "Add TikTok Pixel ID (e.g., D1AL6OBC77U8DLILBH10)",
    isValid: isValidMarketingPixelId,
    settings: { pattern: MARKETING_PIXEL_ID_PATTERN, validate: (tagData) => isValidMarketingPixelId(tagData.value) },
  },
  spotify: {
    placeholder: "Add Spotify Pixel ID (e.g., f049a46c229c4b00a8a9b4687e80df1c)",
    isValid: isValidMarketingPixelId,
    settings: { maxTags: 1, pattern: MARKETING_PIXEL_ID_PATTERN, validate: (tagData) => isValidMarketingPixelId(tagData.value) },
  },
};

const MarketingPixelTagsInput = ({ kind, value, onChange, placeholder, "aria-label": ariaLabel }) => {
  const pixelKind = MARKETING_PIXEL_KINDS[kind];
  const tagifyRef = useRef(null);

  // Tagify types into its own editable box, not the input React renders, so the name goes there.
  useEffect(() => {
    const input = tagifyRef.current && tagifyRef.current.DOM && tagifyRef.current.DOM.input;
    if (input && ariaLabel) input.setAttribute("aria-label", ariaLabel);
  }, [ariaLabel]);

  return (
    <Tags
      tagifyRef={tagifyRef}
      className="w-100"
      placeholder={placeholder || pixelKind.placeholder}
      settings={{
        ...pixelKind.settings,
        duplicates: false,
        addTagOnBlur: true,
        dropdown: { enabled: false },
      }}
      defaultValue={value}
      onChange={(e) => {
        const newPixels = e.detail.tagify.value;
        const validPixels = newPixels.filter((pixel) => pixelKind.isValid(pixel.value));

        if (validPixels.length !== newPixels.length) {
          setTimeout(() => {
            e.detail.tagify.removeAllTags();
            e.detail.tagify.addTags(validPixels);
          }, 100);
        }

        onChange(validPixels);
      }}
    />
  );
};

MarketingPixelTagsInput.propTypes = {
  kind: PropTypes.oneOf(Object.keys(MARKETING_PIXEL_KINDS)).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  "aria-label": PropTypes.string,
};

export default MarketingPixelTagsInput;
